import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/utils/apiClient';
import { Users, AlertCircle, Info, Shield, CheckCircle } from 'lucide-react';

interface ShareWithCommunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  sessionTitle: string;
}

export const ShareWithCommunityModal: React.FC<ShareWithCommunityModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  sessionTitle
}) => {
  const { toast } = useToast();
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [hasExplicitLanguage, setHasExplicitLanguage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const response = await apiClient.post('/community-sessions/submit', {
        session_id: sessionId,
        is_anonymous: isAnonymous,
        content_warning: hasExplicitLanguage ? 'PG' : null
      });

      if (response.success) {
        setSubmissionComplete(true);
        toast({
          title: "Submitted for Review!",
          description: response.message,
          duration: 5000,
        });

        // Reset and close after a delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        toast({
          title: "Submission Failed",
          description: response.message,
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error('Error submitting session to community:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to submit session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsAnonymous(false);
    setHasExplicitLanguage(false);
    setSubmissionComplete(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-5 w-5 text-primary" />
            <DialogTitle>Share with Community</DialogTitle>
          </div>
          <DialogDescription>
            Submit "{sessionTitle}" to the community library for others to enjoy
          </DialogDescription>
        </DialogHeader>

        {!submissionComplete ? (
          <div className="space-y-4 py-4">
            {/* Information Alert */}
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Your session will be reviewed by our team before appearing publicly. This helps ensure a
                positive experience for all community members.
              </AlertDescription>
            </Alert>

            {/* Privacy Options */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="anonymous"
                  checked={isAnonymous}
                  onCheckedChange={(checked) => setIsAnonymous(!!checked)}
                />
                <Label
                  htmlFor="anonymous"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Share anonymously
                </Label>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                Your name won't be shown. The session will appear as "Community Member"
              </p>
            </div>

            {/* Content Warning */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="explicit"
                  checked={hasExplicitLanguage}
                  onCheckedChange={(checked) => setHasExplicitLanguage(!!checked)}
                />
                <Label
                  htmlFor="explicit"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Contains explicit language (PG rating)
                </Label>
              </div>
              <p className="text-xs text-muted-foreground pl-6">
                Check this if your session contains explicit language. It will be marked with a PG warning.
              </p>
            </div>

            {/* Community Guidelines */}
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong className="block mb-1">Community Guidelines</strong>
                <ul className="text-xs space-y-1 list-disc list-inside">
                  <li>No racist, antagonistic, or extremist content</li>
                  <li>Respectful and inclusive language encouraged</li>
                  <li>Explicit language allowed with PG rating</li>
                  <li>Focus on positive, helpful content</li>
                </ul>
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="py-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-100 dark:bg-green-900 p-3">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Submitted Successfully!</h3>
              <p className="text-sm text-muted-foreground">
                Your session is under review. You'll be notified when it's approved.
              </p>
            </div>
          </div>
        )}

        {!submissionComplete && (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit for Review"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
